import { BRAND, NIGHT_STEPS, ROLES, type RoleId } from '@onuw/roles-data'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import { GUIDES } from '../content/guides'
import { RichText, RoleLink } from '../content/RichText'
import {
  type CopyPlay,
  copyPlays,
  EXPANSION_GUIDE,
  rolesOfExpansion,
  wakeNeighbours,
  wakePlaces,
  wakesAtDusk,
} from '../content/roleFacts'
import { ROLE_GUIDES } from '../content/roleGuides'
import { guidePath, roleOfSlug, rolePath } from '../content/slugs'
import { AdSlot } from '../features/ads/AdSlot'
import { AD_SLOTS } from '../lib/config'
import { i18next } from '../lib/i18n'
import { breadcrumbLd, pageMeta } from '../lib/pageMeta'
import { Card } from '../ui/Card'
import { Prose } from '../ui/Prose'
import type { Route } from './+types/role'
import NotFound, { notFoundMeta } from './not-found'
import styles from './role.module.css'

const titleOf = (role: RoleId): string => i18next.t(`roles:${role}.title`)

export function meta({ params }: Route.MetaArgs) {
  const role = roleOfSlug(params.slug)
  if (!role) return notFoundMeta()
  const title = titleOf(role)
  const path = rolePath(role)
  return pageMeta({
    title: `${title}: how the role works and how to play it`,
    description: `${title} in ${BRAND.name}: ${ROLE_GUIDES[role].lead} Team, place in the night order, rulings and tips.`,
    path,
    jsonLd: [
      breadcrumbLd([
        { name: 'Roles', path: '/roles' },
        { name: title, path },
      ]),
    ],
  })
}

/** How she plays a copied role's night, by the kinds of step the role has. */
const COPY_TEXT: Record<CopyPlay, string> = {
  immediately: 'plays its action at once, inside her own turn',
  with: 'wakes with the group when it is called',
  after: 'wakes again on her own, right after the original',
  none: 'takes no action of her own and is whatever the card became',
}

function Facts({ role }: { role: RoleId }) {
  const { t } = useTranslation()
  const def = ROLES[role]
  const places = wakePlaces(role)
  const setup = [
    ...(def.requires ?? []).map((r) => `Needs a ${titleOf(r.role)} card in the deck`),
    ...(def.extraCenter ? ['Adds a fourth card to the center'] : []),
    ...(def.tags.includes('marks') ? ['Puts marks in play: every player starts with a Mark of Clarity'] : []),
    ...(def.tags.includes('needsNumbers') ? ['Gives every player a number'] : []),
  ]
  return (
    <dl className={styles.facts}>
      <dt>Team</dt>
      <dd className={styles.team} data-team={def.team}>
        {t(`ui:teams.${def.team}`)}
      </dd>
      <dt>From</dt>
      <dd>
        <Link to={guidePath(EXPANSION_GUIDE[def.expansion])}>{t(`ui:expansions.${def.expansion}`)}</Link>
      </dd>
      <dt>Wakes</dt>
      <dd>
        {places.length === 0
          ? 'Does not wake'
          : `${wakesAtDusk(role) ? 'At dusk, ' : ''}#${places.join(', then again at #')} of ${NIGHT_STEPS.length}`}{' '}
        (<Link to={guidePath('night-order')}>the night order</Link>)
      </dd>
      <dt>In a deck</dt>
      <dd>
        {def.min === 2
          ? 'Two cards, always together'
          : (def.max ?? 1) > 1
            ? `Up to ${def.max} cards`
            : 'One card'}
      </dd>
      {setup.length > 0 && (
        <>
          <dt>Setup</dt>
          <dd>{setup.join('. ')}</dd>
        </>
      )}
    </dl>
  )
}

/** A list of roles as links: "Seer, Robber, Troublemaker". */
function RoleLinks({ roles }: { roles: RoleId[] }) {
  return (
    <>
      {roles.map((r, i) => (
        <Fragment key={r}>
          {i > 0 && ', '}
          <RoleLink id={r} />
        </Fragment>
      ))}
    </>
  )
}

export default function RolePage() {
  const { t } = useTranslation()
  const role = roleOfSlug(useParams().slug)
  if (!role) return <NotFound />
  const def = ROLES[role]
  const guide = ROLE_GUIDES[role]
  const title = t(`roles:${role}.title`)
  // "a second Count", not "a second The Count"
  const bare = title.replace(/^The /, '')
  const plays = copyPlays(role)
  const { before, after } = wakeNeighbours(role)
  const siblings = rolesOfExpansion(def.expansion).filter((r) => r !== role)
  const expansion = GUIDES[EXPANSION_GUIDE[def.expansion]]
  return (
    <Prose title={title} lead={guide.lead} crumbs={[{ to: '/roles/', label: 'Roles' }]}>
      <div className={styles.top}>
        <div className={styles.card}>
          <Card
            role={role}
            width="100%"
            sizes="(min-width: 561px) 200px, 200px"
            wakeOrder={wakePlaces(role)[0]}
          />
        </div>
        <Facts role={role} />
      </div>

      <h2>What it does</h2>
      {guide.does.map((p) => (
        <p key={p}>
          <RichText text={p} />
        </p>
      ))}

      <h2>Rules and edge cases</h2>
      <ul>
        {guide.rules.map((p) => (
          <li key={p}>
            <RichText text={p} />
          </li>
        ))}
      </ul>

      <h2>How to play it</h2>
      <ul>
        {guide.tips.map((p) => (
          <li key={p}>
            <RichText text={p} />
          </li>
        ))}
      </ul>

      <h2>When it is in the deck</h2>
      <ul>
        {guide.against.map((p) => (
          <li key={p}>
            <RichText text={p} />
          </li>
        ))}
      </ul>

      {role !== 'doppelganger' && (
        <>
          <h2>If the Doppelgänger copies it</h2>
          <p>
            A <RoleLink id="doppelganger" /> who looks at this card becomes a second {bare}.{' '}
            {plays.length === 0
              ? 'She sleeps through the rest of the night, as the original does.'
              : `She ${plays.map((p) => COPY_TEXT[p]).join(', and ')}.`}
          </p>
        </>
      )}

      <h2>Around it</h2>
      {(before || after) && (
        <p>
          {before && (
            <>
              Wakes just before it: <RoleLink id={before} />.{' '}
            </>
          )}
          {after && (
            <>
              Wakes just after it: <RoleLink id={after} />.
            </>
          )}
        </p>
      )}
      {siblings.length > 0 && (
        <p>
          More from <Link to={guidePath(expansion.slug)}>{expansion.title.replace(/^The /, 'the ')}</Link>:{' '}
          <RoleLinks roles={siblings} />.
        </p>
      )}
      <p>
        See <Link to="/roles/">every role</Link>, or read <Link to="/rules/">how to play</Link>.
      </p>
      <AdSlot slot={AD_SLOTS.content} />
    </Prose>
  )
}
